import { useEffect, useState } from "react";
import { Loader2, Save, Plus, Trash2, GripVertical } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { OVERLINE, H1, btnPrimary, Panel, useAdmin } from "@/components/admin/ui";

const inp = "w-full border border-[#E5E2DC] bg-white px-2.5 py-1.5 text-sm outline-none focus:border-[#1F2A22]";

function LinkRows({ rows, onChange, testid }) {
  const set = (i, k, v) => onChange(rows.map((r, j) => (j === i ? { ...r, [k]: v } : r)));
  return (
    <div className="space-y-1.5">
      {rows.map((r, i) => (
        <div key={i} className="flex items-center gap-1.5" data-testid={`${testid}-row-${i}`}>
          <GripVertical size={13} className="text-[#C7C2B8] shrink-0" />
          <input value={r.label || ""} onChange={(e) => set(i, "label", e.target.value)} className={inp} placeholder="Label" data-testid={`${testid}-label-${i}`} />
          <input value={r.href || ""} onChange={(e) => set(i, "href", e.target.value)} className={inp} placeholder="/path or https://…" data-testid={`${testid}-href-${i}`} />
          <button onClick={() => onChange(rows.filter((_, j) => j !== i))} className="p-1 text-red-600 hover:bg-red-50 shrink-0" title="Remove"><Trash2 size={14} /></button>
        </div>
      ))}
      <button onClick={() => onChange([...rows, { label: "", href: "" }])} className="text-xs text-[#FF6A00] font-semibold mt-1" data-testid={`${testid}-add`}><Plus size={11} className="inline" /> Add link</button>
    </div>
  );
}

export default function AdminNavigation() {
  const { admin } = useAdmin();
  const canEdit = admin?.permissions?.includes("website");
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.adminSiteContent().then(setData).catch(() => setData(null)); }, []);

  if (!data) return <div className="py-20 grid place-items-center"><Loader2 className="animate-spin text-[#1F2A22]" /></div>;

  const nav = data.nav || [];
  const footer = data.footer_links || [];
  const setNav = (n) => setData({ ...data, nav: n });
  const setFooter = (f) => setData({ ...data, footer_links: f });

  const save = async () => {
    setBusy(true);
    try {
      const updated = await api.adminSiteContentUpdate({ nav, footer_links: footer });
      setData(updated); toast.success("Navigation updated — it's live now");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to save"); } finally { setBusy(false); }
  };

  return (
    <div className="p-6 md:p-8 max-w-5xl" data-testid="admin-navigation">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div><span className={OVERLINE}>Website</span><h1 className={`${H1} mt-1`}>Navigation Manager</h1></div>
        {canEdit && <button onClick={save} disabled={busy} className={btnPrimary} data-testid="nav-save">{busy ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Save & publish</button>}
      </div>
      {!canEdit && <p className="mt-4 text-sm text-[#8A5A44]">You have read-only access to this page.</p>}

      <div className="grid lg:grid-cols-[1fr_320px] gap-6 mt-6">
        <div className="space-y-6">
          {/* Header menu */}
          <Panel title="Header menu (mega-menu groups)">
            <p className="text-xs text-[#5C6B61] mb-3">These groups appear in the site&apos;s main menu. Reorder is top-to-bottom.</p>
            <div className="space-y-4">
              {nav.map((g, gi) => (
                <div key={gi} className="border border-[#E5E2DC] p-3" data-testid={`nav-group-${gi}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <input value={g.label || ""} onChange={(e) => setNav(nav.map((x, j) => (j === gi ? { ...x, label: e.target.value } : x)))} className={`${inp} font-semibold`} placeholder="Group name" data-testid={`nav-group-label-${gi}`} />
                    <button onClick={() => setNav(nav.filter((_, j) => j !== gi))} className="p-1 text-red-600 hover:bg-red-50 shrink-0" title="Remove group"><Trash2 size={15} /></button>
                  </div>
                  <LinkRows rows={g.items || []} onChange={(items) => setNav(nav.map((x, j) => (j === gi ? { ...x, items } : x)))} testid={`nav-items-${gi}`} />
                </div>
              ))}
            </div>
            <button onClick={() => setNav([...nav, { label: "New group", items: [] }])} className="text-sm text-[#FF6A00] font-semibold mt-3" data-testid="nav-add-group"><Plus size={13} className="inline" /> Add group</button>
          </Panel>

          {/* Footer links */}
          <Panel title="Footer link columns">
            <div className="space-y-4">
              {footer.map((c, ci) => (
                <div key={ci} className="border border-[#E5E2DC] p-3" data-testid={`footer-col-${ci}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <input value={c.title || ""} onChange={(e) => setFooter(footer.map((x, j) => (j === ci ? { ...x, title: e.target.value } : x)))} className={`${inp} font-semibold`} placeholder="Column title" data-testid={`footer-col-title-${ci}`} />
                    <button onClick={() => setFooter(footer.filter((_, j) => j !== ci))} className="p-1 text-red-600 hover:bg-red-50 shrink-0" title="Remove column"><Trash2 size={15} /></button>
                  </div>
                  <LinkRows rows={c.links || []} onChange={(links) => setFooter(footer.map((x, j) => (j === ci ? { ...x, links } : x)))} testid={`footer-links-${ci}`} />
                </div>
              ))}
            </div>
            <button onClick={() => setFooter([...footer, { title: "New column", links: [] }])} className="text-sm text-[#FF6A00] font-semibold mt-3" data-testid="footer-add-col"><Plus size={13} className="inline" /> Add column</button>
          </Panel>
        </div>

        {/* Live preview */}
        <div className="lg:sticky lg:top-6 self-start">
          <p className={OVERLINE}>Live preview</p>
          <div className="mt-2 bg-[#1A1A1A] text-[#F3EFE6] p-4 rounded" data-testid="nav-preview">
            <p className="text-[10px] uppercase tracking-wide text-[#FF6A00] mb-2">Header menu</p>
            <div className="space-y-2.5">
              {nav.map((g, i) => (
                <div key={i}>
                  <p className="font-serif text-sm">{g.label || "—"}</p>
                  <div className="flex flex-wrap gap-x-2 gap-y-0.5 mt-0.5">
                    {(g.items || []).map((it, j) => <span key={j} className="text-[11px] text-[#F3EFE6]/55">{it.label || "—"}</span>)}
                  </div>
                </div>
              ))}
            </div>
            <p className="text-[10px] uppercase tracking-wide text-[#FF6A00] mt-4 mb-2">Footer</p>
            <div className="grid grid-cols-2 gap-2">
              {footer.map((c, i) => (
                <div key={i}>
                  <p className="text-[11px] font-semibold">{c.title || "—"}</p>
                  {(c.links || []).map((l, j) => <p key={j} className="text-[10px] text-[#F3EFE6]/55">{l.label || "—"}</p>)}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
